import { useEffect } from "react";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

/**
 * Capture UTM params eagerly at module load time (before React mounts).
 * This ensures we don't miss params if the URL gets rewritten.
 */
function captureUtmFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const hasAnyUtm = UTM_KEYS.some((key) => params.has(key));
    if (!hasAnyUtm) return;

    // First-touch: don't overwrite existing values
    if (localStorage.getItem("utm_source")) return;

    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        localStorage.setItem(key, value);
      }
    });
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

// Run immediately when this module is imported
captureUtmFromUrl();

/**
 * Hook version — also attempts capture on mount as a safety net.
 */
export function useUtmCapture() {
  useEffect(() => {
    captureUtmFromUrl();
  }, []);
}

/**
 * Reads stored UTM values from localStorage.
 * Returns null values if nothing is stored.
 */
export function getStoredUtm(): Record<string, string | null> {
  return {
    utm_source: localStorage.getItem("utm_source"),
    utm_medium: localStorage.getItem("utm_medium"),
    utm_campaign: localStorage.getItem("utm_campaign"),
  };
}

/**
 * Clears UTM values from localStorage after they've been written to the DB.
 */
export function clearStoredUtm() {
  localStorage.removeItem("utm_source");
  localStorage.removeItem("utm_medium");
  localStorage.removeItem("utm_campaign");
}
